import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TradeCard } from 'store/services/trade/tradeApi';
import { FoilCardImage } from 'components/FoilCardImage';
import { AssetUrls } from 'config/assets';
import { formatLanguage } from 'constants/languages';
import './SendCardTooltip.css';
import { CardFinish, wantAcceptedFinishes, finishFromRow } from 'utils/finishes';
import { FinishTag } from 'components/FinishTag';

// URL construction helpers
const getSetIconUrl = (code: string): string =>
  `https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons/${code.toLowerCase()}.svg`;

const CONDITION_DISPLAY: Record<string, string> = {
  'NEAR_MINT': 'NM',
  'SLIGHTLY_PLAYED': 'SP',
  'MODERATELY_PLAYED': 'MP',
  'HEAVILY_PLAYED': 'HP',
  'DAMAGED': 'DMG',
};

interface SendCardTooltipProps {
  cardData: TradeCard;
  /** Legacy positioning: the pre-computed top-left point (Send page call sites). */
  position?: { x: number; y: number };
  /**
   * Drawer positioning (opt-in): the trigger card's bounding box. When provided, the
   * tooltip sits flush with the card's right edge, 16px below its top, and flips to the
   * card's left edge when a rightward drawer would clip the viewport. Takes precedence
   * over `position`. Used by the Trade Alerts page; Send-page callers omit it and keep
   * the legacy `position` behavior unchanged.
   */
  triggerRect?: { left: number; right: number; top: number };
  username: string;
  onClose: () => void;
  onSetHover: (setCode: string | null) => void;
  hoveredSetCode: string | null;
  /** When true, renders a card image to the left of the tooltip (used by list view) */
  showImage?: boolean;
}

const PLACEHOLDER_IMAGE = AssetUrls.search.imageComing;

export const SendCardTooltip: React.FC<SendCardTooltipProps> = ({
  cardData,
  position,
  triggerRect,
  username,
  onClose,
  onSetHover,
  hoveredSetCode,
  showImage = false,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position ?? { x: 0, y: 0 });
  const [isFlipped, setIsFlipped] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [failedSetIcons, setFailedSetIcons] = useState<Set<string>>(new Set());

  // Debounce tooltip appearance (200ms delay)
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Edge detection for positioning - flush to card edge (no gap)
  useEffect(() => {
    if (!tooltipRef.current || !isVisible) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Drawer mode (Trade Alerts): position relative to the trigger card's bounding box.
    // Flush with the card's right edge, 8px below its top, flipping to the card's left
    // edge when a rightward drawer would clip the viewport. The flip decision uses the
    // measured tooltip width (rect.width), so it's robust to S/M/L card sizes.
    if (triggerRect) {
      const SAFETY = 16;
      const flip = triggerRect.right + rect.width > viewport.width - SAFETY;
      const x = flip ? triggerRect.left - rect.width : triggerRect.right;
      let y = triggerRect.top + 8;

      // Bottom/top edge clamp so the panel stays on screen.
      if (y + rect.height > viewport.height - 20) {
        y = viewport.height - rect.height - 20;
      }
      if (y < 20) {
        y = 20;
      }

      setIsFlipped(flip);
      setAdjustedPosition({ x, y });
      return;
    }

    // Legacy mode (Send page): `position` is already the right edge of the card, so x is
    // flush; clamp to the viewport without flipping.
    let { x, y } = position ?? { x: 0, y: 0 };

    // Right edge detection - if tooltip would go off screen, we can't show on left easily
    // Just clamp to viewport
    if (x + rect.width > viewport.width - 10) {
      x = viewport.width - rect.width - 10;
    }

    // Bottom edge detection
    if (y + rect.height > viewport.height - 20) {
      y = viewport.height - rect.height - 20;
    }

    // Top edge detection
    if (y < 20) {
      y = 20;
    }

    setAdjustedPosition({ x, y });
  }, [position, triggerRect, isVisible]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onClose]);

  // Determine finish display — show what the receiver WANTS (not intersected with physical availability)
  const finishBadges = useMemo((): CardFinish[] => {
    // If both are undefined, backend didn't provide preference data → "Any finish"
    if (cardData.acceptFoil == null && cardData.acceptNonFoil == null) {
      return [];
    }
    return wantAcceptedFinishes(cardData);
  }, [cardData]);

  const handleSetIconError = (code: string) => {
    setFailedSetIcons((prev) => new Set(prev).add(code));
  };

  if (!isVisible) {
    return null;
  }

  const tooltipContent = (
    <div
      className={`send-card-tooltip-portal ${showImage ? 'with-image' : ''} ${triggerRect ? 'drawer' : ''} ${isFlipped ? 'flipped' : ''}`}
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
      ref={tooltipRef}
    >
      {showImage && (
        <div className="send-card-tooltip-image">
          <FoilCardImage
            src={cardData.imageUrl || PLACEHOLDER_IMAGE}
            alt={cardData.name}
            finish={finishFromRow(cardData)}
            borderRadius="8px 0 0 8px"
            loading="eager"
          />
        </div>
      )}
      <div className={`send-card-tooltip ${showImage ? 'with-image' : ''}`}>
        {/* Username Header */}
        <div className="tooltip-header">
          <span className="tooltip-username">{username}</span>
          <span className="tooltip-wants"> wants:</span>
        </div>

        {/* Sets Section */}
        <div className="tooltip-section">
          <div className="tooltip-section-label">Sets</div>
          <div className="tooltip-set-icons">
            {cardData.acceptableSetDetails?.length ? (
              <>
                {cardData.acceptableSetDetails.slice(0, 25).map((set) => (
                  !failedSetIcons.has(set.code) && (
                    <img
                      // cardId is the unique member of acceptableSetDetails
                      // (set.code repeats when a set has multiple accepted
                      // printings, e.g. two Ninth Edition collector numbers)
                      key={set.cardId}
                      src={getSetIconUrl(set.code)}
                      alt={set.name}
                      title={set.name}
                      className={`tooltip-set-icon ${hoveredSetCode === set.code ? 'hovered' : ''}`}
                      onMouseEnter={() => onSetHover(set.code)}
                      onMouseLeave={() => onSetHover(null)}
                      onError={() => handleSetIconError(set.code)}
                    />
                  )
                ))}
                {cardData.acceptableSetDetails.length > 25 && (
                  <span className="tooltip-more-sets">
                    +{cardData.acceptableSetDetails.length - 25} more
                  </span>
                )}
              </>
            ) : (
              <span className="tooltip-any-badge">Any set</span>
            )}
          </div>
        </div>

        {/* Finishes Section */}
        <div className="tooltip-section">
          <div className="tooltip-section-label">Finishes</div>
          <div className="tooltip-badges">
            {finishBadges.length > 0 ? (
              finishBadges.map((finish) => (
                <FinishTag key={finish} finish={finish} size="cell" />
              ))
            ) : (
              <span className="tooltip-any-badge">Any finish</span>
            )}
          </div>
        </div>

        {/* Languages Section */}
        <div className="tooltip-section">
          <div className="tooltip-section-label">Accepted Languages</div>
          <div className="tooltip-badges">
            {cardData.acceptableLanguages?.length ? (
              cardData.acceptableLanguages.map((lang) => (
                <span key={lang} className="tooltip-badge language">
                  {formatLanguage(lang)}
                </span>
              ))
            ) : (
              <span className="tooltip-any-badge">Any language</span>
            )}
          </div>
        </div>

        {/* Conditions Section - only show if restricted */}
        {cardData.acceptableConditions?.length && (
          <div className="tooltip-section">
            <div className="tooltip-section-label">Conditions</div>
            <div className="tooltip-badges">
              {cardData.acceptableConditions.map((cond) => (
                <span key={cond} className="tooltip-badge condition">
                  {CONDITION_DISPLAY[cond] || cond}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export default SendCardTooltip;
